import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Optional, Output } from '@angular/core';
import { DataType, DateTime } from '@mode-switch/express';
import { FieldFormatModalService } from '@mode/charts/contract';
import { ChartTypes } from '@mode/shared/contract-common';
import { FormatListItem } from '../format-list-item';
import {
  ShortcutMenu,
  ShortcutOption,
  getOptionByShortcutTypeOrFirst,
  getShortcutMenu,
} from './format-shortcut-menu.helpers';

@Component({
  selector: 'mode-format-shortcut-menu',
  templateUrl: './format-shortcut-menu.component.html',
  styleUrls: ['./format-shortcut-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormatShortcutMenuComponent implements OnChanges {
  @Input() field!: FormatListItem;
  @Input() format!: ChartTypes.TextFormat;
  @Output() update = new EventEmitter<Partial<ChartTypes.TextFormat>>();

  /**
   * @description Get the formula data type of the provided field
   */
  get dataType(): DataType.Name | undefined {
    return this.field?.dataType;
  }

  /**
   * @description Get the DateTime Unit of the provided field; the field will
   * have a unit if either formula is a DATEPART() or DATETRUNC()
   */
  get dateTimeUnit(): DateTime.Unit | undefined {
    return this.field?.dateTimeUnit;
  }

  /**
   * @description Get the shortcutType of the provided format (TextFormat)
   */
  get shortcutType(): ChartTypes.FormatShortcutType {
    return this.format?.shortcutType as ChartTypes.FormatShortcutType;
  }

  /**
   * @description Get field discrete state
   */
  get isDiscrete(): boolean {
    return this.field?.isDiscrete;
  }

  /**
   * @description Format shortcut menu with display label and corresponding shortcut menu options
   */
  menu!: ShortcutMenu;

  /**
   * @description Selected format shortcut option
   */
  selectedMenuItem!: ShortcutOption;

  constructor(@Optional() private modal: FieldFormatModalService) {}

  ngOnChanges() {
    this.updateMenu();
  }

  /**
   * @description Updates the menu per the current data type and time unit
   */
  updateMenu() {
    this.menu = getShortcutMenu(this.dataType as DataType.Name, this.isDiscrete, this.dateTimeUnit);
    this.selectedMenuItem = getOptionByShortcutTypeOrFirst(this.menu.options, this.shortcutType);
  }

  /**
   * @description Handle FormatShortcutType selection, emitting the new
   * TextFormat properties per the selection
   */
  selectItem(item: ShortcutOption) {
    this.selectedMenuItem = item;
    if (item.shortcut) {
      this.update.emit({
        ...item.shortcut.format,
        colType: this.dataType,
      });
    }
  }

  /**
   * @description Launch the formats modal
   */
  showMoreFormats() {
    this.modal?.open({
      title: this.field?.displayName,
      colType: this.dataType ?? DataType.Name.Null,
      format: this.format || this.selectedMenuItem.shortcut?.format,
      applyCustomFormat: ({ textFormat }) => {
        this.update.emit({
          ...textFormat,
        });
      },
    });
  }
}
